import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map, throwError } from 'rxjs';
import type { ApiResponse } from '@shared-models';
import { toCuitApiValue } from '@shared-models';
import { API_CONFIG, type ApiConfig } from '../api-config';

/** verificationStatus del backend. */
export type CustomerIdentificationVerificationStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'AUTO_PASSED'
  | 'NEEDS_REVIEW'
  | 'REJECTED'
  | string;

/**
 * Respuesta data de identificación (ApiResponse.data).
 * Campos documentados: id, customerId, countryId, countryName, identificationId,
 * identificationCode, identificationName, procedureNumberRequired, number,
 * procedureNumber, uniqueCode, provinceCode, issuedAt, expiresAt, frontUrl, backUrl,
 * primary, verificationStatus, verificationScore, rejectionReason, verifiedAt, …
 */
export interface CustomerIdentificationDto {
  id: string;
  customerId?: string;
  countryId: string;
  countryName?: string;
  identificationId: string;
  identificationCode?: string;
  identificationName?: string;
  procedureNumberRequired?: boolean;
  number?: string;
  procedureNumber?: string | null;
  uniqueCode?: string;
  provinceCode?: string | null;
  /** ISO date o yyyy-MM-dd. */
  expiresAt?: string;
  /** ISO date o yyyy-MM-dd — fecha de emisión. */
  issuedAt?: string;
  frontUrl?: string | null;
  backUrl?: string | null;
  primary?: boolean;
  verificationStatus?: CustomerIdentificationVerificationStatus;
  verificationScore?: number | null;
  rejectionReason?: string | null;
  verifiedAt?: string | null;
  /** Enriquecimiento review-queue */
  customerCode?: string;
  customerName?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Ítem de la cola de revisión. */
export type CustomerIdentificationReviewQueueItem = CustomerIdentificationDto & {
  customerId: string;
};

/**
 * Campos textuales multipart (POST create / PUT update).
 * Nombres de part exactamente como el backend (camelCase).
 */
export type CustomerIdentificationWritePayload = {
  countryId: string;
  identificationId: string;
  number: string;
  uniqueCode: string;
  /** yyyy-MM-dd */
  expiresAt: string;
  /** yyyy-MM-dd — fecha de emisión */
  issuedAt?: string;
  /** Condicional si procedureNumber del title del país = true. */
  procedureNumber?: string | null;
  provinceCode?: string | null;
  /** Si es el primer registro, el backend/force local manda true. */
  primary?: boolean;
};

/**
 * POST `.../{id}/review` — JSON (no multipart).
 * decision: APPROVE | REJECT; rejectionReason obligatorio si REJECT.
 */
export type CustomerIdentificationReviewDecision = 'APPROVE' | 'REJECT';

export type CustomerIdentificationReviewPayload = {
  decision: CustomerIdentificationReviewDecision;
  rejectionReason?: string | null;
};

export type CustomerIdentificationReviewQueueQuery = {
  page?: number;
  size?: number;
  status?: string;
  search?: string;
};

export type CustomerIdentificationReviewQueuePage = {
  items: CustomerIdentificationReviewQueueItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
};

/** Respuesta de POST .../identifications/ocr-preview */
export interface CustomerIdentificationOcrPreviewDto {
  previewId: string;
  provider?: string;
  /** Número de documento → identNumeroDocumento. */
  number?: string | null;
  /** Número de trámite → identNumeroTramite. */
  procedureNumber?: string | null;
  /** CUIL → identCuil. */
  cuil?: string | null;
  /** Fecha de emisión → identFechaEmision. */
  issuedAt?: string | null;
  /** Alias legacy de emisión (si no viene issuedAt). */
  birthdate?: string | null;
  /** Fecha de vencimiento → identFechaVencimiento. */
  expiresAt?: string | null;
  /** Alias legacy; preferir `expiresAt`. */
  expiresAtPreview?: string | null;
  /** Nombre detectado (UI junto a confidence). */
  fullName?: string | null;
  countryHint?: string | null;
  signatureDetected?: boolean;
  /** Confianza OCR 0–1 o 0–100 (UI junto a fullName). */
  confidence?: number | null;
  frontUrl?: string | null;
  backUrl?: string | null;
}

/** JPEG / PNG / WebP — ~10 KB a 8 MB (contrato POST/PUT). */
export const CUSTOMER_IDENT_IMAGE_MIN_BYTES = 10 * 1024;
export const CUSTOMER_IDENT_IMAGE_MAX_BYTES = 8 * 1024 * 1024;
export const CUSTOMER_IDENT_IMAGE_MIME = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
]);

export function isCustomerIdentificationImageValid(file: File): boolean {
  if (!file) return false;
  const type = (file.type || '').toLowerCase();
  const byMime = CUSTOMER_IDENT_IMAGE_MIME.has(type);
  const byExt = /\.(jpe?g|png|webp)$/i.test(file.name || '');
  if (!byMime && !byExt) return false;
  return (
    file.size >= CUSTOMER_IDENT_IMAGE_MIN_BYTES &&
    file.size <= CUSTOMER_IDENT_IMAGE_MAX_BYTES
  );
}

export function customerIdentificationImageError(file: File | null | undefined): string | null {
  if (!file) return 'Archivo requerido';
  const type = (file.type || '').toLowerCase();
  const byMime = CUSTOMER_IDENT_IMAGE_MIME.has(type);
  const byExt = /\.(jpe?g|png|webp)$/i.test(file.name || '');
  if (!byMime && !byExt) {
    return 'Formato inválido (JPEG, PNG o WebP)';
  }
  if (file.size < CUSTOMER_IDENT_IMAGE_MIN_BYTES) {
    return 'El archivo debe pesar al menos ~10 KB';
  }
  if (file.size > CUSTOMER_IDENT_IMAGE_MAX_BYTES) {
    return 'El archivo no debe superar 8 MB';
  }
  return null;
}

/** Normaliza fecha de form a yyyy-MM-dd. */
export function toIdentificationExpiresAt(value: string): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const dmy = raw.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (dmy) {
    const dd = dmy[1].padStart(2, '0');
    const mm = dmy[2].padStart(2, '0');
    return `${dmy[3]}-${mm}-${dd}`;
  }
  return raw;
}

/**
 * Identificaciones del cliente — paridad web con móvil + admin.
 *
 * POST multipart parts: countryId, identificationId, number, uniqueCode,
 * expiresAt (yyyy-MM-dd), procedureNumber?, provinceCode?, primary?, front*, back*
 * PUT: mismos campos textuales; front/back opcionales (reemplazan y re-verifican).
 * POST review JSON: { decision: APPROVE|REJECT, rejectionReason? }
 */
@Injectable({ providedIn: 'root' })
export class CustomerIdentificationService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private customerResource(customerId: string): string {
    return `${this.apiConfig.apiUrl}/customers/${customerId}/identifications`;
  }

  private reviewQueueResource(): string {
    return `${this.apiConfig.apiUrl}/customers/identifications/review-queue`;
  }

  /** GET /customers/{customerId}/identifications */
  list(customerId: string): Observable<CustomerIdentificationDto[]> {
    return this.http
      .get<
        ApiResponse<
          CustomerIdentificationDto[] | { content?: CustomerIdentificationDto[] }
        >
      >(this.customerResource(customerId))
      .pipe(map((res) => this.unwrapList(res.data)));
  }

  /** GET /customers/{customerId}/identifications/{id} */
  getById(
    customerId: string,
    id: string,
  ): Observable<CustomerIdentificationDto> {
    return this.http
      .get<ApiResponse<CustomerIdentificationDto>>(
        `${this.customerResource(customerId)}/${id}`,
      )
      .pipe(map((res) => res.data as CustomerIdentificationDto));
  }

  /**
   * POST /customers/{customerId}/identifications
   * multipart — front y back obligatorios (JPEG/PNG/WebP, ~10 KB–8 MB).
   */
  create(
    customerId: string,
    payload: CustomerIdentificationWritePayload,
    files: { front: File; back: File },
  ): Observable<CustomerIdentificationDto> {
    const frontErr = customerIdentificationImageError(files.front);
    const backErr = customerIdentificationImageError(files.back);
    if (frontErr || backErr) {
      return throwError(
        () =>
          new Error(
            frontErr
              ? `Frente: ${frontErr}`
              : `Reverso: ${backErr}`,
          ),
      );
    }

    return this.http
      .post<ApiResponse<CustomerIdentificationDto>>(
        this.customerResource(customerId),
        this.toFormData(payload, files, { requireFiles: true }),
      )
      .pipe(map((res) => res.data as CustomerIdentificationDto));
  }

  /**
   * PUT /customers/{customerId}/identifications/{id}
   * multipart — front/back opcionales; si vienen, reemplazan y re-disparan verificación.
   */
  update(
    customerId: string,
    id: string,
    payload: CustomerIdentificationWritePayload,
    files?: { front?: File | null; back?: File | null },
  ): Observable<CustomerIdentificationDto> {
    if (files?.front) {
      const err = customerIdentificationImageError(files.front);
      if (err) {
        return throwError(() => new Error(`Frente: ${err}`));
      }
    }
    if (files?.back) {
      const err = customerIdentificationImageError(files.back);
      if (err) {
        return throwError(() => new Error(`Reverso: ${err}`));
      }
    }

    return this.http
      .put<ApiResponse<CustomerIdentificationDto>>(
        `${this.customerResource(customerId)}/${id}`,
        this.toFormData(payload, files, { requireFiles: false }),
      )
      .pipe(map((res) => res.data as CustomerIdentificationDto));
  }

  /**
   * POST /customers/{customerId}/identifications/ocr-preview
   * form-data: front, back, countryId, identificationId → data OCR para prellenar.
   */
  ocrPreview(
    customerId: string,
    files: { front: File; back: File },
    meta: { countryId: string; identificationId: string },
  ): Observable<CustomerIdentificationOcrPreviewDto> {
    const frontErr = customerIdentificationImageError(files.front);
    const backErr = customerIdentificationImageError(files.back);
    if (frontErr || backErr) {
      return throwError(
        () =>
          new Error(
            frontErr ? `Frente: ${frontErr}` : `Reverso: ${backErr}`,
          ),
      );
    }

    const countryId = (meta.countryId ?? '').trim();
    const identificationId = (meta.identificationId ?? '').trim();
    if (!countryId || !identificationId) {
      return throwError(
        () =>
          new Error(
            'Seleccioná país y tipo de documento antes del preview OCR',
          ),
      );
    }

    const formData = new FormData();
    formData.append('front', files.front, files.front.name || 'front.jpg');
    formData.append('back', files.back, files.back.name || 'back.jpg');
    formData.append('countryId', countryId);
    formData.append('identificationId', identificationId);

    return this.http
      .post<ApiResponse<CustomerIdentificationOcrPreviewDto>>(
        `${this.customerResource(customerId)}/ocr-preview`,
        formData,
      )
      .pipe(map((res) => this.normalizeOcrPreviewDto(res.data)));
  }

  /** Acepta camelCase / snake_case del OCR. */
  private normalizeOcrPreviewDto(
    data: CustomerIdentificationOcrPreviewDto | null | undefined,
  ): CustomerIdentificationOcrPreviewDto {
    const raw = (data ?? {}) as CustomerIdentificationOcrPreviewDto &
      Record<string, unknown>;
    return {
      previewId: String(raw.previewId ?? raw['preview_id'] ?? '').trim(),
      provider: (raw.provider as string | undefined) ?? undefined,
      number: (raw.number as string | null | undefined) ?? null,
      procedureNumber:
        (raw.procedureNumber as string | null | undefined) ??
        (raw['procedure_number'] as string | null | undefined) ??
        null,
      cuil:
        (raw.cuil as string | null | undefined) ??
        (raw['unique_code'] as string | null | undefined) ??
        (raw['uniqueCode'] as string | null | undefined) ??
        null,
      issuedAt:
        (raw.issuedAt as string | null | undefined) ??
        (raw['issued_at'] as string | null | undefined) ??
        null,
      birthdate:
        (raw.birthdate as string | null | undefined) ??
        (raw['birth_date'] as string | null | undefined) ??
        null,
      expiresAt:
        (raw.expiresAt as string | null | undefined) ??
        (raw['expires_at'] as string | null | undefined) ??
        (raw.expiresAtPreview as string | null | undefined) ??
        null,
      expiresAtPreview:
        (raw.expiresAtPreview as string | null | undefined) ?? null,
      fullName:
        (raw.fullName as string | null | undefined) ??
        (raw['full_name'] as string | null | undefined) ??
        null,
      countryHint:
        (raw.countryHint as string | null | undefined) ??
        (raw['country_hint'] as string | null | undefined) ??
        null,
      signatureDetected:
        (raw.signatureDetected as boolean | undefined) ??
        (raw['signature_detected'] as boolean | undefined),
      confidence:
        typeof raw.confidence === 'number' ? raw.confidence : null,
      frontUrl:
        (raw.frontUrl as string | null | undefined) ??
        (raw['front_url'] as string | null | undefined) ??
        null,
      backUrl:
        (raw.backUrl as string | null | undefined) ??
        (raw['back_url'] as string | null | undefined) ??
        null,
    };
  }

  /** DELETE /customers/{customerId}/identifications/{id} */
  delete(customerId: string, id: string): Observable<void> {
    return this.http
      .delete<ApiResponse<unknown>>(
        `${this.customerResource(customerId)}/${id}`,
      )
      .pipe(map(() => undefined));
  }

  /**
   * POST /customers/{customerId}/identifications/{id}/review
   * JSON (no multipart): { decision: "APPROVE"|"REJECT", rejectionReason? }
   * rejectionReason obligatorio si decision === REJECT.
   */
  review(
    customerId: string,
    id: string,
    payload: CustomerIdentificationReviewPayload,
  ): Observable<CustomerIdentificationDto> {
    const decision = payload.decision;
    if (decision !== 'APPROVE' && decision !== 'REJECT') {
      return throwError(
        () => new Error('decision inválida (APPROVE | REJECT)'),
      );
    }

    const body: { decision: CustomerIdentificationReviewDecision; rejectionReason?: string } =
      { decision };

    if (decision === 'REJECT') {
      const reason = (payload.rejectionReason ?? '').trim();
      if (!reason) {
        return throwError(
          () =>
            new Error(
              'rejectionReason es obligatorio cuando decision es REJECT',
            ),
        );
      }
      body.rejectionReason = reason;
    }

    return this.http
      .post<ApiResponse<CustomerIdentificationDto>>(
        `${this.customerResource(customerId)}/${id}/review`,
        body,
      )
      .pipe(map((res) => res.data as CustomerIdentificationDto));
  }

  /**
   * GET /customers/identifications/review-queue
   */
  listReviewQueue(
    query: CustomerIdentificationReviewQueueQuery = {},
  ): Observable<CustomerIdentificationReviewQueuePage> {
    let params = new HttpParams()
      .set('page', String(query.page ?? 0))
      .set('size', String(query.size ?? 20));
    if (query.status?.trim()) {
      params = params.set('status', query.status.trim());
    }
    if (query.search?.trim()) {
      params = params.set('search', query.search.trim());
    }

    return this.http
      .get<
        ApiResponse<
          | CustomerIdentificationReviewQueueItem[]
          | {
              content?: CustomerIdentificationReviewQueueItem[];
              page?: number;
              size?: number;
              totalElements?: number;
              totalPages?: number;
            }
        >
      >(this.reviewQueueResource(), { params })
      .pipe(map((res) => this.unwrapQueuePage(res.data, query)));
  }

  /**
   * Arma multipart/form-data con nombres de part del contrato.
   * bool primary → "true" | "false".
   * expiresAt normalizado a yyyy-MM-dd.
   */
  private toFormData(
    payload: CustomerIdentificationWritePayload,
    files?: { front?: File | null; back?: File | null },
    options?: { requireFiles?: boolean },
  ): FormData {
    const formData = new FormData();
    formData.append('countryId', String(payload.countryId).trim());
    formData.append('identificationId', String(payload.identificationId).trim());
    formData.append('number', String(payload.number).trim());
    formData.append('uniqueCode', toCuitApiValue(payload.uniqueCode));
    formData.append(
      'expiresAt',
      toIdentificationExpiresAt(payload.expiresAt),
    );

    const issuedAt = toIdentificationExpiresAt(payload.issuedAt || '');
    if (issuedAt) {
      formData.append('issuedAt', issuedAt);
    }

    const procedure = (payload.procedureNumber ?? '').toString().trim();
    if (procedure) {
      formData.append('procedureNumber', procedure);
    }

    const province = (payload.provinceCode ?? '').toString().trim();
    if (province) {
      formData.append('provinceCode', province);
    }

    if (payload.primary !== undefined && payload.primary !== null) {
      formData.append('primary', payload.primary ? 'true' : 'false');
    }

    if (files?.front) {
      formData.append('front', files.front, files.front.name || 'front.jpg');
    } else if (options?.requireFiles) {
      // create exige fronts en el contrato; la validación previa lanza error
    }
    if (files?.back) {
      formData.append('back', files.back, files.back.name || 'back.jpg');
    }

    return formData;
  }

  private unwrapList(
    data:
      | CustomerIdentificationDto[]
      | { content?: CustomerIdentificationDto[] }
      | null
      | undefined,
  ): CustomerIdentificationDto[] {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.content)) return data.content;
    return [];
  }

  private unwrapQueuePage(
    data:
      | CustomerIdentificationReviewQueueItem[]
      | {
          content?: CustomerIdentificationReviewQueueItem[];
          page?: number;
          size?: number;
          totalElements?: number;
          totalPages?: number;
        }
      | null
      | undefined,
    query: CustomerIdentificationReviewQueueQuery,
  ): CustomerIdentificationReviewQueuePage {
    const page = query.page ?? 0;
    const size = query.size ?? 20;

    if (Array.isArray(data)) {
      return {
        items: data,
        page,
        size,
        totalElements: data.length,
        totalPages: Math.ceil(data.length / size) || 1,
      };
    }

    const items = data?.content ?? [];
    return {
      items,
      page: data?.page ?? page,
      size: data?.size ?? size,
      totalElements: data?.totalElements ?? items.length,
      totalPages:
        data?.totalPages ??
        (Math.ceil((data?.totalElements ?? items.length) / size) || 1),
    };
  }
}
